import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import MuiTableHead from '@mui/material/TableHead';
import MuiTableBody from '@mui/material/TableBody';
import MuiTableRow from '@mui/material/TableRow';

import { Text } from '../Text';

import { TableWrapper, StyledCell, StyledTable, StyledRow } from './Table.styles';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';

function Table(props) {
  const { minTableWidth, blackList, rows, showHeader, headerCellsConfig, children, contentCellsConfig, rowStyles } =
    props;

  const [globalBlackList, setGlobalBlackList] = useState(['id']);

  useEffect(() => {
    /** Add passed blackList items to globalBlackList array */
    const newBlackList = globalBlackList.concat(blackList);

    if (globalBlackList.length !== newBlackList.length) {
      setGlobalBlackList({ globalBlackList: newBlackList });
    }
  }, []);

  const renderHead = () =>
    rows && showHeader ? (
      <MuiTableHead>
        <MuiTableRow>
          {rows.map((row) => {
            const { id, label } = row;
            const cellConfigProps = get(headerCellsConfig, `${id}`, {});
            return (
              <StyledCell key={id} {...cellConfigProps}>
                <Text.CopyMini noMargins>{label}</Text.CopyMini>
              </StyledCell>
            );
          })}
        </MuiTableRow>
      </MuiTableHead>
    ) : undefined;

  const renderBody = () =>
    children ? (
      <MuiTableBody>
        {children.map((child) => (
          <StyledRow key={child.id} style={rowStyles}>
            {Object.entries(child).map(([key, item]) => {
              let cell;
              if (!globalBlackList.includes(key)) {
                const cellConfigProps = get(contentCellsConfig, `${key}`, {});
                cell = (
                  <StyledCell key={key} {...cellConfigProps}>
                    {item}
                  </StyledCell>
                );
              }
              return cell;
            })}
          </StyledRow>
        ))}
      </MuiTableBody>
    ) : undefined;

  return (
    <TableWrapper>
      <StyledTable minTableWidth={minTableWidth}>
        {renderHead()}
        {renderBody()}
      </StyledTable>
    </TableWrapper>
  );
}

Table.propTypes = {
  /** Tell table to ignore certain object keys - 'id' gets ignored by default */
  blackList: PropTypes.arrayOf(PropTypes.string),
  /** Array of content objects to display in table */
  children: ReactChildrenPropType,
  /** Object with keys matching row object IDs containing @MUITableCell properties and valid td attributes */
  contentCellsConfig: PropTypes.shape({}),
  /** Object with keys matching row object IDs containing @MUITableCell properties and valid td attributes */
  headerCellsConfig: PropTypes.shape({}),
  /** Min width of the table passed via string (900px default) */
  minTableWidth: PropTypes.string,
  /** Array of label objects to display at the top of the table */
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      width: PropTypes.string
    })
  ).isRequired,
  showHeader: PropTypes.bool,
  rowStyles: PropTypes.shape({})
};

Table.defaultProps = {
  blackList: [],
  children: undefined,
  contentCellsConfig: {},
  headerCellsConfig: {},
  minTableWidth: '900px',
  showHeader: true,
  rowStyles: undefined
};

export default Table;
